import type { ReactNode } from "react";
import { css } from "@g4rcez/components";
import { ChatCircleDotsIcon, PaperclipIcon } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { AIMessage } from "@/store/repositories/electron/ai.repository";

const AI_MESSAGE_LOADING_MESSAGES = [
    "Thinking...",
    "Analyzing your notes...",
    "Preparing a markdown response...",
    "Checking the workspace context...",
];

export const AI_CHAT_LOADING_MESSAGES = AI_MESSAGE_LOADING_MESSAGES;

function getLoadingMessage(index: number): string {
    return AI_MESSAGE_LOADING_MESSAGES[index % AI_MESSAGE_LOADING_MESSAGES.length] ?? AI_MESSAGE_LOADING_MESSAGES[0]!;
}

function isErrorMessage(message: AIMessage): boolean {
    return message.content.trim().toLowerCase().startsWith("error:");
}

function formatFileSize(size: number): string {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} KiB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MiB`;
}

function MessageAttachments({ message }: { message: AIMessage }) {
    if (!message.files?.length) return null;
    return (
        <ul aria-label="Attached files" className="mt-2 flex max-w-full flex-wrap gap-1.5">
            {message.files.map((file) => (
                <li
                    key={file.id}
                    className="flex max-w-full items-center gap-1 rounded-full border border-current/25 px-2 py-1 text-xs"
                >
                    <PaperclipIcon size={12} className="shrink-0" aria-hidden="true" />
                    <span className="max-w-40 truncate">{file.name}</span>
                    <span className="shrink-0 opacity-75">{formatFileSize(file.size)}</span>
                    <span className="sr-only">
                        Attachment metadata only; the original file is unavailable after reopening this chat.
                    </span>
                </li>
            ))}
        </ul>
    );
}

function AIAssistantMarkdown({ content, tone = "default" }: { content: string; tone?: "default" | "inherit" }) {
    return (
        <div
            className={css(
                "ai-markdown max-w-[72ch] text-base leading-7 text-foreground",
                tone === "inherit" && "ai-markdown--inherit",
            )}
        >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </div>
    );
}

function AssistantRenderedBody({
    content,
    isStreaming,
    loadingIndex,
    hasWorkspaceData,
}: {
    content: string;
    isStreaming: boolean;
    loadingIndex: number;
    hasWorkspaceData: boolean;
}) {
    if (content.trim().length > 0) {
        return <AIAssistantMarkdown content={content} />;
    }
    if (isStreaming) {
        return <p className="text-sm text-muted-foreground italic">{getLoadingMessage(loadingIndex)}</p>;
    }
    if (hasWorkspaceData) return null;
    return <p className="text-sm text-muted-foreground italic">No assistant response was returned.</p>;
}

function hasWorkspaceData(message: AIMessage): boolean {
    const data = message.workspaceData;
    return Boolean(data && (data.activities.length > 0 || data.sources.length > 0 || data.proposals.length > 0));
}

export function AIChatMessageItem({
    message,
    isStreaming = false,
    loadingIndex,
    maxWidthClass = "max-w-safe",
    assistantFooter = null,
}: {
    message: AIMessage;
    isStreaming?: boolean;
    loadingIndex: number;
    maxWidthClass?: string;
    assistantFooter?: ReactNode;
}) {
    if (message.role === "system") {
        const error = isErrorMessage(message);
        const content = error ? message.content.replace(/^error:\s*/i, "") : message.content;

        return (
            <aside
                role={error ? "alert" : "status"}
                className={css(
                    "rounded-xl border px-3 py-2 text-xs leading-5",
                    error
                        ? "border-danger/40 bg-danger-subtle text-danger"
                        : "border-card-border bg-muted text-muted-foreground",
                    maxWidthClass,
                )}
            >
                <p className="font-medium">{error ? "Chat error" : "Chat status"}</p>
                <p>{content}</p>
            </aside>
        );
    }

    if (message.role === "user") {
        return (
            <article
                aria-label="Your message"
                className="w-fit max-w-[min(88%,42rem)] break-words rounded-2xl rounded-br-md bg-button-primary-bg px-4 py-3 text-sm leading-6 text-button-primary-text"
            >
                {message.content ? <AIAssistantMarkdown content={message.content} tone="inherit" /> : null}
                <MessageAttachments message={message} />
            </article>
        );
    }

    return (
        <article
            aria-label="Assistant message"
            className={css(
                "flex w-full min-w-0 flex-col gap-4 rounded-xl border border-card-border bg-card-background/50 px-4 py-4 text-foreground sm:px-5",
                maxWidthClass,
            )}
        >
            <header className="flex items-center gap-2">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <ChatCircleDotsIcon aria-hidden="true" className="size-4" />
                </span>
                <p className="text-sm font-semibold text-foreground">Assistant</p>
            </header>
            <div className="min-w-0">
                <AssistantRenderedBody
                    content={message.content}
                    isStreaming={isStreaming}
                    loadingIndex={loadingIndex}
                    hasWorkspaceData={hasWorkspaceData(message)}
                />
            </div>
            {assistantFooter ? <div className="border-t border-card-border pt-3">{assistantFooter}</div> : null}
        </article>
    );
}

export function AIChatMessageList({
    messages,
    isStreaming,
    loadingMessageIndex,
    renderActions,
    maxWidthClass = "max-w-3xl",
}: {
    messages: AIMessage[];
    isStreaming: boolean;
    loadingMessageIndex: number;
    renderActions?: (message: AIMessage) => ReactNode;
    maxWidthClass?: string;
}) {
    return (
        <div className="flex flex-col gap-6">
            {messages.map((message, index) => {
                const isLastAssistantStreaming =
                    isStreaming && index === messages.length - 1 && message.role === "assistant";
                const withAction = renderActions?.(message) ?? null;
                const assistantFooter = message.role === "assistant" ? withAction : null;
                return (
                    <div
                        key={message.id}
                        className={css(
                            "flex w-full flex-col gap-2",
                            message.role === "user" && "items-end",
                            message.role === "assistant" && "items-start",
                            message.role === "system" && "items-center",
                        )}
                    >
                        <AIChatMessageItem
                            message={message}
                            maxWidthClass={maxWidthClass}
                            loadingIndex={loadingMessageIndex}
                            isStreaming={isLastAssistantStreaming}
                            assistantFooter={assistantFooter}
                        />
                        {message.role !== "assistant" && withAction ? (
                            <div className={css("mt-2", maxWidthClass)}>{withAction}</div>
                        ) : null}
                    </div>
                );
            })}
            {isStreaming && messages.length > 0 && messages[messages.length - 1]?.role === "user" ? (
                <div className="flex justify-start">
                    <div
                        className={css(
                            "bg-secondary-background rounded-xl rounded-bl-md border border-card-border px-4 py-3 text-sm text-muted-foreground",
                            maxWidthClass,
                        )}
                    >
                        {getLoadingMessage(loadingMessageIndex)}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
